import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useAudienceToken from "../../../../datahooks/useAudienceToken";

import type { AudienceSearchResponse } from "../../../../datahooks/useAudienceSearch";
import type {
  AudienceFilters,
  AudienceProfile,
  AudienceStats,
} from "../../../../types";

const wsBaseUrl = import.meta.env.VITE_CORE_WS_URL;

export default function useAudienceProgress(
  filters: AudienceFilters,
  isMiningTargets?: boolean,
  pagingParams?: { page: number; pageSize: number },
) {
  const socketRef = useRef<WebSocket | null>(null);

  const { audienceToken } = useAudienceToken();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { id: campaignId, custom_variable_keys: customVariables } =
    useCampaignContext();

  const queryClient = useQueryClient();
  const { campaignsKeys } = getQueryKeys(workspaceId);

  const url = `${wsBaseUrl}campaigns/${campaignId}/audience?authorization=${audienceToken}`;

  const handlePeopleMessage = useCallback(
    (profiles: AudienceProfile[]) => {
      // On the Backend side, the custom variables are only added to the campaign, once there are some valid profiles
      // Because of that, for list campaign we need to invalidate the "Campaign Details" data in that case
      if (profiles.length > 0 && customVariables.length === 0) {
        queryClient.invalidateQueries({
          queryKey: campaignsKeys.details(campaignId),
        });
      }

      const page = pagingParams?.page ?? 0;
      const pageSize = pagingParams?.pageSize ?? 24;
      const audienceSearchKey = campaignsKeys.audienceSearch(
        campaignId,
        filters,
        { page, pageSize },
      );
      const cachedAudienceSearch =
        queryClient.getQueryData<AudienceSearchResponse>(audienceSearchKey);

      if (!cachedAudienceSearch) return;

      const existingData = cachedAudienceSearch.data ?? [];
      const urnSet = new Set(existingData.map(({ urn }) => urn));
      const filteredNew = profiles.filter(({ urn }) => !urnSet.has(urn));

      if (!filteredNew.length) return;

      queryClient.setQueryData<AudienceSearchResponse>(
        audienceSearchKey,
        (prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            data: [...filteredNew, ...(prev.data ?? [])],
            total: (prev.total ?? 0) + filteredNew.length,
          };
        },
      );
    },
    [
      campaignId,
      customVariables.length,
      filters,
      pagingParams?.page,
      pagingParams?.pageSize,
    ],
  );

  const handleStatusMessage = useCallback(
    (audienceStats: AudienceStats) => {
      const audienceStatsKey = campaignsKeys.audienceStats(campaignId);
      if (queryClient.getQueryData(audienceStatsKey)) {
        queryClient.setQueryData(audienceStatsKey, audienceStats);
      }
    },
    [campaignId],
  );

  const connect = useCallback(() => {
    const websocket = new WebSocket(url);
    socketRef.current = websocket;

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.type === "people") handlePeopleMessage(message.data);
      if (message.type === "status") handleStatusMessage(message);
    };
  }, [url, handlePeopleMessage, handleStatusMessage]);

  const reconnectSocket = useCallback(() => {
    socketRef.current?.close();
    connect();
  }, [connect]);

  useEffect(() => {
    if (!audienceToken || !isMiningTargets) return;

    connect();

    return () => socketRef.current?.close();
  }, [audienceToken, connect, isMiningTargets]);

  return { reconnectSocket };
}
